/**
 * Per-notification-kind rendering — Wave 3 Batch H.
 *
 * Maps each `Notification['kind']` to the body markup that renders inside
 * a bell row. Mirrors legacy `NotificationActions.jsx:14-176` verb set
 * exactly; visual styling is FE-discretion (Linear-feel, restrained
 * palette).
 *
 * The body is a single line: actor name (bold) + verb + bolded card title
 * + optional extras ("from Backlog to Today", "(title, priority)"). The
 * snippet (for comment / mention notifications) renders as a truncated
 * second line, owned by the caller.
 *
 * Unknown kinds fall back to a generic verb so adding kinds on the BE
 * stays non-breaking — exactly the contract the `notifications.kind`
 * column was designed for.
 */

import type { ReactNode } from 'react'
import { Trans } from 'react-i18next'

import type { DeliverableKind, Notification } from '@/api/types-bridge'
import { i18n } from '@/i18n'

/** Pretty-print the deliverable kind for legacy-parity verbs. */
function kindLabel(kind: DeliverableKind | undefined): string {
  const key = kind ?? 'card'
  return i18n.t(`rendering.kindLabel.${key}`, { ns: 'notifications' })
}

/** An agent notification's subject, with a localized stand-in for a payload that predates the name. */
function agentName(name: string | undefined): string {
  return name ?? i18n.t('rendering.fallbackAgentName', { ns: 'notifications' })
}

/** A run refused because the agent's model endpoint needs Premium: a plan pause, not a fault. */
function isPausedRun(p: Notification['payload']): boolean {
  return p.reason === 'private_endpoint_requires_premium'
}

/** Lowercase deliverable kind for verbs (matches legacy parity). */
function kindLabelLower(kind: DeliverableKind | undefined): string {
  const key = kind ?? 'card'
  return i18n.t(`rendering.kindLabelLower.${key}`, { ns: 'notifications' })
}

/**
 * Render the body of a notification row. Returns a React fragment with
 * the verb + title + extras, ready to drop into the bell row.
 *
 * The actor name is rendered separately by the caller (the bell wraps it
 * in a `<span class="font-medium">` adjacent to an avatar) — this function
 * starts with the verb, not the actor.
 *
 * Every string comes from `notifications:rendering.body.*` through
 * `<Trans>`; the catalog carries the emphasis as tags — `<k>` the card
 * kind, `<t>` the title / channel / page, `<m>` a muted trailing detail,
 * `<s>` a status name inside it — so translators can reorder the sentence
 * and the markup follows. The verbs previously lived here as English JSX,
 * which left every bell row untranslated while its aria-label was not.
 */
const BODY_COMPONENTS = {
  k: <strong />,
  t: <strong className="font-semibold text-fg" />,
  m: <span className="text-fg-muted" />,
  s: <em className="not-italic font-medium text-fg-muted" />,
}

function body(key: string, values: Record<string, string | number> = {}): ReactNode {
  return (
    <Trans
      i18n={i18n}
      ns="notifications"
      i18nKey={`rendering.body.${key}`}
      values={values}
      components={BODY_COMPONENTS}
    />
  )
}

/** Muted "(title, priority)" suffix for the update kinds; nothing when no fields changed. */
function changedFieldsSuffix(fields: readonly string[] | undefined): ReactNode {
  if (!fields || fields.length === 0) return null
  return (
    <span className="text-fg-muted">
      {' '}
      {i18n.t('rendering.body.changedFields', { ns: 'notifications', fields: fields.join(', ') })}
    </span>
  )
}

/**
 * `viewerId` lets the collaborator kinds read "added you" for the person
 * who was added or removed, instead of repeating their own name back.
 */
export function renderNotificationBody(n: Notification, viewerId?: string | null): ReactNode {
  const p = n.payload
  const ns = 'notifications'
  const title = p.cardTitle ?? i18n.t('rendering.fallbackCardTitle', { ns })
  const kind = kindLabel(p.cardKind)
  const docTitle = p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns })

  switch (n.kind) {
    case 'mention.comment':
      return body('mentionComment', { kind, title })
    case 'mention.description':
      return body('mentionDescription', { kind, title })
    case 'mention.chat':
      if (p.source === 'all' && p.channelName) {
        return body('mentionChatChannelAll', { channelName: p.channelName })
      }
      return p.conversationType === 'channel' && p.channelName
        ? body('mentionChatChannel', { channelName: p.channelName })
        : body('mentionChatDm')
    case 'mention.wiki':
      return body('mentionWiki', { docTitle })
    case 'wiki_comment.mention':
      return body('wikiCommentMention', { docTitle })
    case 'wiki_comment.reply':
      return body('wikiCommentReply', { docTitle })
    case 'wiki_comment.new':
      return body('wikiCommentNew', { docTitle })
    case 'wiki_comment.resolved':
      return body(p.resolved === false ? 'wikiCommentReopened' : 'wikiCommentResolved', {
        docTitle,
      })
    case 'channel.member_joined':
      return p.channelName
        ? body('channelMemberJoined', { channelName: p.channelName })
        : body('channelMemberJoinedNoName')
    case 'channel.member_left':
      return p.channelName
        ? body('channelMemberLeft', { channelName: p.channelName })
        : body('channelMemberLeftNoName')
    case 'card.assigned':
      return body('cardAssigned', { kind, title })
    case 'update-deliverable':
      return (
        <>
          {body('updateDeliverable', { kind, title })}
          {changedFieldsSuffix(p.changedFields)}
        </>
      )
    case 'move-deliverable':
      return (
        <>
          {body('moveDeliverable', { kind, title })}
          {p.fromStatus && p.toStatus ? (
            <span className="text-fg-muted">
              {' '}
              {body('moveDeliverableStatus', { from: p.fromStatus, to: p.toStatus })}
            </span>
          ) : null}
        </>
      )
    case 'delete-deliverable':
      return body('deleteDeliverable', { kind, title })
    case 'add-collaborator': {
      const role = i18n.t(
        p.cardKind === 'event' ? 'rendering.roleAttendee' : 'rendering.roleCollaborator',
        { ns },
      )
      if (viewerId && p.addedUserId === viewerId)
        return body('addCollaboratorYou', { kind, title, role })
      return body('addCollaborator', {
        kind,
        title,
        who: p.addedUserName ?? i18n.t('rendering.fallbackTargetUser', { ns }),
        role,
      })
    }
    case 'remove-collaborator': {
      const role = i18n.t(
        p.cardKind === 'event' ? 'rendering.roleAttendee' : 'rendering.roleCollaborator',
        { ns },
      )
      if (viewerId && p.removedUserId === viewerId)
        return body('removeCollaboratorYou', { kind, title, role })
      return body('removeCollaborator', {
        kind,
        title,
        who: p.removedUserName ?? i18n.t('rendering.fallbackTargetUser', { ns }),
        role,
      })
    }
    case 'unassign-deliverable':
      return body('unassignDeliverable', {
        kind,
        title,
        who: p.unassignedUserName ?? i18n.t('rendering.fallbackUnassigned', { ns }),
      })
    case 'deliverable-comment':
      return body('deliverableComment', { kind, title })
    case 'comment.reply':
      return body('commentReply', { kind, title })
    case 'create-event':
      return body('createEvent', { kind: kindLabel('event'), title })
    case 'update-event':
      return (
        <>
          {body('updateEvent', { kind: kindLabel('event'), title })}
          {changedFieldsSuffix(p.changedFields)}
        </>
      )
    case 'cancel-event':
      return body('cancelEvent', { kind: kindLabel('event'), title })
    case 'create-announcement':
      return body('createAnnouncement', { kind: kindLabel('announcement'), title })
    case 'update-announcement':
      return (
        <>
          {body('updateAnnouncement', { kind: kindLabel('announcement'), title })}
          {changedFieldsSuffix(p.changedFields)}
        </>
      )
    case 'storage.over_limit':
      return body('storageOverLimit')
    case 'agent.budget_exhausted':
      return body('agentBudgetExhausted', { agentName: agentName(p.agentName) })
    case 'agent.run_failed':
      return body(isPausedRun(p) ? 'agentPaused' : 'agentRunFailed', {
        agentName: agentName(p.agentName),
      })
    default:
      return body('fallback', { title })
  }
}

/**
 * Single-line plaintext fallback for `aria-label` / screen-reader text.
 * Mirrors `renderNotificationBody` in spirit but returns a flat string.
 *
 * Resolves through the `notifications:rendering.verbs.*` catalog so the
 * label follows the user's chosen locale, as the JSX body above does via
 * `rendering.body.*`.
 */
export function renderNotificationText(n: Notification, viewerId?: string | null): string {
  const p = n.payload
  const cardTitle = p.cardTitle ?? i18n.t('rendering.fallbackCardTitle', { ns: 'notifications' })
  const kindWord = kindLabelLower(p.cardKind)
  const actorBase = p.actorName ?? i18n.t('rendering.fallbackActor', { ns: 'notifications' })
  // The text form feeds the row's accessible name, which has no room for a
  // pill: an agent actor reads "Name · AI" there.
  const actor =
    p.actorKind === 'agent'
      ? i18n.t('aiTag.nameWithTag', { ns: 'common', name: actorBase })
      : actorBase
  const ns = 'notifications'

  switch (n.kind) {
    case 'mention.comment':
      return i18n.t('rendering.verbs.mentionCommentText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'mention.description':
      return i18n.t('rendering.verbs.mentionDescriptionText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'mention.chat':
      if (p.source === 'all' && p.channelName) {
        return i18n.t('rendering.verbs.mentionChatChannelAllText', {
          ns,
          actor,
          channelName: p.channelName,
        })
      }
      return p.conversationType === 'channel' && p.channelName
        ? i18n.t('rendering.verbs.mentionChatChannelText', {
            ns,
            actor,
            channelName: p.channelName,
          })
        : i18n.t('rendering.verbs.mentionChatDmText', { ns, actor })
    case 'mention.wiki':
      // Phase 4c — wiki body @mention. Catalog string in `en/es`
      // already exists (`mentionWikiText`); the BE trigger stamps
      // `docId` + `docTitle` on the payload, and the deep-link click
      // hook below routes to `/wiki/d/<docId>`.
      return i18n.t('rendering.verbs.mentionWikiText', {
        ns,
        actor,
        docTitle:
          p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns, defaultValue: 'a page' }),
      })
    case 'wiki_comment.mention':
      return i18n.t('rendering.verbs.wikiCommentMentionText', {
        ns,
        actor,
        docTitle:
          p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns, defaultValue: 'a page' }),
      })
    case 'wiki_comment.reply':
      return i18n.t('rendering.verbs.wikiCommentReplyText', {
        ns,
        actor,
        docTitle:
          p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns, defaultValue: 'a page' }),
      })
    case 'wiki_comment.new':
      return i18n.t('rendering.verbs.wikiCommentNewText', {
        ns,
        actor,
        docTitle:
          p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns, defaultValue: 'a page' }),
      })
    case 'wiki_comment.resolved': {
      // The BE fires this kind for BOTH resolve and reopen; `payload.resolved`
      // is the discriminator (true = resolved, false = reopened).
      const verbKey =
        p.resolved === false
          ? 'rendering.verbs.wikiCommentReopenedText'
          : 'rendering.verbs.wikiCommentResolvedText'
      return i18n.t(verbKey, {
        ns,
        actor,
        docTitle:
          p.docTitle ?? i18n.t('rendering.fallbackDocTitle', { ns, defaultValue: 'a page' }),
      })
    }
    case 'channel.member_joined':
      return p.channelName
        ? i18n.t('rendering.verbs.channelMemberJoinedText', {
            ns,
            actor,
            channelName: p.channelName,
          })
        : i18n.t('rendering.verbs.channelMemberJoinedNoNameText', { ns, actor })
    case 'channel.member_left':
      return p.channelName
        ? i18n.t('rendering.verbs.channelMemberLeftText', {
            ns,
            actor,
            channelName: p.channelName,
          })
        : i18n.t('rendering.verbs.channelMemberLeftNoNameText', { ns, actor })
    case 'card.assigned':
      return i18n.t('rendering.verbs.cardAssignedText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'update-deliverable':
      return i18n.t('rendering.verbs.updateDeliverableText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'move-deliverable':
      return p.fromStatus && p.toStatus
        ? i18n.t('rendering.verbs.moveDeliverableWithStatusText', {
            ns,
            actor,
            kind: kindWord,
            title: cardTitle,
            from: p.fromStatus,
            to: p.toStatus,
          })
        : i18n.t('rendering.verbs.moveDeliverableText', {
            ns,
            actor,
            kind: kindWord,
            title: cardTitle,
          })
    case 'delete-deliverable':
      return i18n.t('rendering.verbs.deleteDeliverableText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'add-collaborator': {
      const role =
        p.cardKind === 'event'
          ? i18n.t('rendering.roleAttendee', { ns })
          : i18n.t('rendering.roleCollaborator', { ns })
      if (viewerId && p.addedUserId === viewerId) {
        return i18n.t('rendering.verbs.addCollaboratorYouText', {
          ns,
          actor,
          role,
          kind: kindWord,
          title: cardTitle,
        })
      }
      const who = p.addedUserName ?? i18n.t('rendering.fallbackTargetUser', { ns })
      return i18n.t('rendering.verbs.addCollaboratorText', {
        ns,
        actor,
        who,
        role,
        kind: kindWord,
        title: cardTitle,
      })
    }
    case 'remove-collaborator': {
      const role =
        p.cardKind === 'event'
          ? i18n.t('rendering.roleAttendee', { ns })
          : i18n.t('rendering.roleCollaborator', { ns })
      if (viewerId && p.removedUserId === viewerId) {
        return i18n.t('rendering.verbs.removeCollaboratorYouText', {
          ns,
          actor,
          role,
          kind: kindWord,
          title: cardTitle,
        })
      }
      const who = p.removedUserName ?? i18n.t('rendering.fallbackTargetUser', { ns })
      return i18n.t('rendering.verbs.removeCollaboratorText', {
        ns,
        actor,
        who,
        role,
        kind: kindWord,
        title: cardTitle,
      })
    }
    case 'unassign-deliverable':
      return i18n.t('rendering.verbs.unassignDeliverableText', {
        ns,
        actor,
        who: p.unassignedUserName ?? i18n.t('rendering.fallbackUnassigned', { ns }),
        kind: kindWord,
        title: cardTitle,
      })
    case 'deliverable-comment':
      return i18n.t('rendering.verbs.deliverableCommentText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'comment.reply':
      return i18n.t('rendering.verbs.commentReplyText', {
        ns,
        actor,
        kind: kindWord,
        title: cardTitle,
      })
    case 'create-event':
      return i18n.t('rendering.verbs.createEventText', { ns, actor, title: cardTitle })
    case 'update-event':
      return i18n.t('rendering.verbs.updateEventText', { ns, actor, title: cardTitle })
    case 'cancel-event':
      return i18n.t('rendering.verbs.cancelEventText', { ns, actor, title: cardTitle })
    case 'create-announcement':
      return i18n.t('rendering.verbs.createAnnouncementText', { ns, actor, title: cardTitle })
    case 'update-announcement':
      return i18n.t('rendering.verbs.updateAnnouncementText', { ns, actor, title: cardTitle })
    case 'storage.over_limit':
      return i18n.t('rendering.verbs.storageOverLimitText', { ns })
    case 'agent.budget_exhausted':
      return i18n.t('rendering.verbs.agentBudgetExhaustedText', {
        ns,
        agentName: agentName(p.agentName),
      })
    case 'agent.run_failed':
      return i18n.t(
        isPausedRun(p) ? 'rendering.verbs.agentPausedText' : 'rendering.verbs.agentRunFailedText',
        { ns, agentName: agentName(p.agentName) },
      )
    default:
      return i18n.t('rendering.verbs.fallbackText', { ns, actor, title: cardTitle })
  }
}
